import { useMemo, forwardRef } from "react";
import {
  Box,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  ListSubheader
} from "@material-ui/core";
import { RecommendOutlined } from "@material-ui/icons";
import Label from "components/Label";
import { groupPropertyTypes } from "utils/listings/propertyTypeGroups";
import { CheckedMenuItemLabel } from "./CheckedMenuItemLabel";
import type { ReactNode, SyntheticEvent } from "react";
import type { SelectChangeEvent } from "@material-ui/core";

// ──────────────────────────────────────────────────────────────────────────────
// "סוג נכס" picker — the flat list split into labelled sections (דירות /
// צמודי קרקע / מסחרי / אחר) via `ListSubheader`.
//
// Deliberately a drop-in for `SimpleMultiSelect`: same chip rendering, same
// `string[]` value shape, so swapping it in changes nothing downstream — the
// Algolia filter builder still receives the raw Hebrew strings it always did.
//
// The taxonomy itself lives in utils/listings/propertyTypeGroups.ts so it can
// be unit-tested and reused by non-Select surfaces (checkbox lists, chips).
//
// `recommendedOptions` optionally pins a "המלצות" section above those groups.
// It is purely additive — a recommended type is ALSO listed in its normal
// group, so it is reachable from two rows of one menu. Callers that pass
// nothing get the exact pre-existing menu.
// ──────────────────────────────────────────────────────────────────────────────

/** Section title for the pinned recommendations, and the badge each one carries. */
export const RECOMMENDED_GROUP_LABEL = "המלצות";
export const RECOMMENDED_BADGE_LABEL = "המלצה";

type BaseProps = {
  /** Available options — typically Algolia facet values from `usePropertyTypes`. */
  options: readonly string[];
  /**
   * Options to ALSO pin at the top of the menu under a "המלצות" header, each
   * badged with a green recommendation icon.
   *
   * Deliberately additive: a recommended option keeps its normal place inside
   * its taxonomy group, so the same value appears twice in one menu. Both
   * copies reflect (and toggle) the SAME underlying value — MUI builds the next
   * value from the clicked item's `value`, not from its position.
   *
   * `options` stays the source of truth: anything here that isn't in `options`
   * is ignored rather than pinned, since a pinned entry with no real option
   * behind it would render an unselectable row. Duplicates within the list are
   * collapsed. Omit (or pass an empty list) and the menu renders exactly as it
   * did before this prop existed.
   */
  recommendedOptions?: readonly string[];
  label?: string;
  /** Label shown while nothing is selected (e.g. "כל הסוגים"). */
  emptyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  small?: boolean;
  /** Map an option to its display text. Options are Hebrew already, so this is
   *  only needed if a caller feeds code-based values (e.g. `ListingType`). */
  getOptionLabel?: (option: string) => string;
};

type MultiProps = BaseProps & {
  multiple?: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type SingleProps = BaseProps & {
  multiple: false;
  value: string;
  onChange: (value: string) => void;
};

export type PropertyTypeSelectProps = MultiProps | SingleProps;

/**
 * Keep a chip's delete click from reaching the Select (which would open the
 * menu). Equivalent to `preventBubbling()` from utils/listings/helper, inlined
 * because that module transitively pulls in moment, firebase/compat and the
 * Natively mobile SDK — far too much weight for a leaf input meant to be
 * imported across features.
 */
const swallowEvent = (event: SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Inert section header.
 *
 * This MUI version's `SelectInput` clones EVERY child with `role="option"`, a
 * click handler and `aria-selected` — it has no `ListSubheader` guard (the
 * `muiSkipListHighlight` escape hatch only exists in later versions), and
 * `cloneElement` props win over the child's own. Left as a bare
 * `<ListSubheader>`, each group title is therefore announced to screen readers
 * as a selectable option and takes keyboard focus ahead of the real options.
 *
 * Destructuring only `children` drops every injected prop, so the header
 * renders inert. `disabled` is set at the call site because MUI's `MenuList`
 * reads `child.props.disabled` when choosing which item to focus first — that
 * is what hands initial focus to the first real option instead of a title.
 */
const GroupHeader = forwardRef<HTMLLIElement, { children?: ReactNode; disabled?: boolean }>(
  ({ children }, ref) => (
    <ListSubheader
      ref={ref}
      role="presentation"
      tabIndex={-1}
      sx={{ fontWeight: 700, lineHeight: "36px" }}
    >
      {children}
    </ListSubheader>
  )
);

/**
 * The "המלצה" badge on a pinned item.
 *
 * The meaning must not ride on the green alone — colour-only signalling is
 * invisible to colour-blind users and to screen readers. `titleAccess` renders
 * an SVG `<title>`, which puts the word into the option's accessible name; the
 * wrapping `title` attribute is what produces the hover tooltip (an SVG title
 * is not surfaced as a native tooltip by browsers).
 */
const RecommendedBadge = (): JSX.Element => (
  <Box component="span" title={RECOMMENDED_BADGE_LABEL} sx={{ display: "inline-flex", flex: "none" }}>
    <RecommendOutlined
      titleAccess={RECOMMENDED_BADGE_LABEL}
      sx={{ fontSize: 18, color: "success.main" }}
    />
  </Box>
);

export function PropertyTypeSelect(props: PropertyTypeSelectProps): JSX.Element {
  const { options, recommendedOptions, emptyLabel, disabled, loading, small, getOptionLabel } = props;
  const multiple = props.multiple !== false;

  const groups = useMemo(() => groupPropertyTypes(options), [options]);

  // Pin only real options, at most once each, in the caller's order.
  const recommended = useMemo(() => {
    if (!recommendedOptions?.length) return [];
    const available = new Set(options);
    return Array.from(new Set(recommendedOptions)).filter((option) => available.has(option));
  }, [recommendedOptions, options]);

  const selected: string[] = multiple
    ? (props.value as string[]) ?? []
    : props.value
    ? [props.value as string]
    : [];

  const label = selected.length === 0 && emptyLabel ? emptyLabel : props.label;

  const emit = (next: string[]): void => {
    if (props.multiple === false) props.onChange(next[0] ?? "");
    else props.onChange(next);
  };

  const handleChange = ({ target }: SelectChangeEvent<string[] | string>): void => {
    const { value } = target;
    emit(typeof value === "string" ? (value ? [value] : []) : value);
  };

  const handleDelete = (option: string) => (): void => {
    emit(selected.filter((item) => item !== option));
  };

  // MUI walks Select's children directly — wrapping a section in a Fragment
  // hides its items from value matching and keyboard nav, so this MUST stay a
  // flat array of elements.
  //
  // The recommended items are a DUPLICATE of rows that also appear in their
  // taxonomy group below, so their keys are namespaced — two siblings sharing a
  // key is a React error, and the reconciler would drop one of them.
  const children = [
    ...(recommended.length > 0
      ? [
          <GroupHeader key="group-recommended" disabled>
            {RECOMMENDED_GROUP_LABEL}
          </GroupHeader>,
          ...recommended.map((option) => (
            <MenuItem value={option} key={`recommended-${option}`}>
              <CheckedMenuItemLabel selected={selected.includes(option)}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <RecommendedBadge />
                  {getOptionLabel ? getOptionLabel(option) : option}
                </Stack>
              </CheckedMenuItemLabel>
            </MenuItem>
          ))
        ]
      : []),
    ...groups.flatMap((group) => [
      <GroupHeader key={`group-${group.id}`} disabled>
        {group.label}
      </GroupHeader>,
      ...group.options.map((option) => (
        <MenuItem value={option} key={option}>
          <CheckedMenuItemLabel selected={selected.includes(option)}>
            {getOptionLabel ? getOptionLabel(option) : option}
          </CheckedMenuItemLabel>
        </MenuItem>
      ))
    ])
  ];

  return (
    <FormControl fullWidth disabled={disabled || loading}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        size={small ? "small" : undefined}
        value={multiple ? selected : selected[0] ?? ""}
        input={<OutlinedInput label={label} />}
        disabled={disabled || loading}
        renderValue={() => (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {selected.map((option) => (
              <Label
                key={option}
                color="primary"
                variant="ghost"
                {...(multiple && {
                  onDelete: handleDelete(option),
                  onMouseDown: swallowEvent
                })}
              >
                {getOptionLabel ? getOptionLabel(option) : option}
              </Label>
            ))}
          </Stack>
        )}
        MenuProps={{ PaperProps: { style: { maxHeight: 410 } } }}
        onChange={handleChange}
      >
        {children}
      </Select>
    </FormControl>
  );
}

export default PropertyTypeSelect;
